import React from 'react';
import PropTypes from 'prop-types';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { AppButton, SimpleModal } from '@Components/Control';

function parseChannels(text) {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(',').map((p) => p.trim());
      const channelUrl = parts[0] || '';
      const email = parts[1] || undefined;
      // eslint-disable-next-line camelcase
      return email ? { channel_url: channelUrl, email } : { channel_url: channelUrl };
    })
    // eslint-disable-next-line camelcase
    .filter((c) => c.channel_url);
}

function AdminYtAuditsCreateModal({
  small, onClose, onSubmit, submitting = false,
}) {
  const { theme: { page, button } } = useAppTheme();
  const [value, setValue] = React.useState('');

  const channels = parseChannels(value);

  const handleChange = React.useCallback((e) => setValue(e.target.value), []);

  const handleSubmit = React.useCallback(() => {
    if (channels.length === 0) return;
    onSubmit(channels);
  }, [channels, onSubmit]);

  return (
    <SimpleModal
      small={small}
      toggleModal={(open) => { if (!open) onClose(); }}
      modalTitle="Submit YT Audits"
      textColor={page.primaryText}
      highlightColor={page.highlight}
      modalBg={page.modalBg}
      hideLowerSection
      desktopMinWidth="42rem"
    >
      <Box direction="column" gap="1rem" pad={{ vertical: '1rem' }}>
        <Text size="0.70rem" color={page.secondaryText}>
          Enter one YouTube channel URL per line. You can optionally append a comma and a
          {' '}
          <Text size="0.70rem" color={page.primaryText}>client email address</Text>
          {' '}
          — this is the email of the rights holder or client the audit is being run for,
          and will be associated with the audit record:
        </Text>
        <Text size="0.75rem" color={page.primaryText} style={{ fontFamily: 'monospace' }}>
          https://youtube.com/@channel, client@example.com
        </Text>
        <textarea
          value={value}
          onChange={handleChange}
          placeholder={'https://youtube.com/@channelA\nhttps://youtube.com/@channelB, user@example.com'}
          rows={10}
          style={{
            width: '100%',
            padding: '0.6rem',
            background: page.inputBg,
            color: page.inputText,
            border: `1px solid ${page.inputBorder}`,
            borderRadius: 'var(--app-radius)',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <Box
          direction="row"
          justify="between"
          align="center"
          border={{
            color: page.tableBorder, size: '1px', style: 'solid', side: 'top',
          }}
          pad={{ top: '1rem' }}
        >
          <Text size="0.82rem" color={page.secondaryText}>
            {channels.length > 0
              ? `${channels.length} channel${channels.length !== 1 ? 's' : ''} detected`
              : 'No channels detected'}
          </Text>
          <Box direction="row" gap="0.5rem">
            <AppButton
              overrideHover
              level="tableControls"
              label="Cancel"
              color={button.primaryText}
              bgColor={button.primaryBg}
              onClick={onClose}
            />
            <AppButton
              overrideHover
              level="tableControls"
              label={submitting ? 'Submitting…' : `Submit ${channels.length > 0 ? channels.length : ''} Audit${channels.length !== 1 ? 's' : ''}`}
              disabled={channels.length === 0 || submitting}
              color={button.primaryText}
              bgColor={page.highlight}
              onClick={handleSubmit}
            />
          </Box>
        </Box>
      </Box>
    </SimpleModal>
  );
}

AdminYtAuditsCreateModal.propTypes = {
  small: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};


export default AdminYtAuditsCreateModal;
